import { Region } from '../types/Condition.type';

/**
 * Canonical region code → display-name map.
 *
 * Replaces the REGIONS_HASH copies that were duplicated in app-level
 * constants files and inline in SoapPDF / patientChartExportGenerate.
 */
export const REGION_CODE_TO_NAME: Record<Region, string> = {
  C: 'Cervical',
  S: 'Shoulder',
  E: 'Elbow',
  'H/W': 'Hand and Wrist',
  T: 'Thoracic',
  L: 'Lumbosacral',
  H: 'Hip',
  K: 'Knee',
  'F/A': 'Foot and Ankle',
  HEAD: 'Head',
  O: 'Other',
  'F/B': 'Full Body',
};

// Reverse lookup: name → code (built once).
const NAME_TO_CODE = new Map<string, Region>(
  (Object.entries(REGION_CODE_TO_NAME) as [Region, string][]).map(([code, name]) => [
    name.toLowerCase(),
    code,
  ])
);

/**
 * True if `value` is a known region code.
 */
const isRegionCode = (value: string): value is Region => value in REGION_CODE_TO_NAME;

/**
 * Resolve a single region value — code or name — to its display name.
 * Returns the raw value as-is when it matches neither.
 */
const resolveOne = (value: string): string => {
  if (isRegionCode(value)) return REGION_CODE_TO_NAME[value];
  const byName = NAME_TO_CODE.get(value.toLowerCase());
  if (byName) return REGION_CODE_TO_NAME[byName];
  return value;
};

/**
 * Given a region value that may be a code, a display name, an array of
 * either, or null/undefined, return a comma-separated display string.
 *
 * Returns `''` when there is nothing to display.
 *
 * Usage:
 *   regionDisplay('F/A')                    // 'Foot and Ankle'
 *   regionDisplay('Foot and Ankle')         // 'Foot and Ankle'
 *   regionDisplay(['C', 'Foot and Ankle'])  // 'Cervical, Foot and Ankle'
 *   regionDisplay(null)                     // ''
 */
export const regionDisplay = (region: string | string[] | null | undefined): string => {
  if (!region) return '';
  const arr = Array.isArray(region) ? region : [region];
  if (arr.length === 0) return '';
  return arr.map(resolveOne).join(', ');
};

/**
 * Normalize a region value (code or name, string or array) to an array of
 * region codes. Useful for form fields that need code values.
 *
 * Returns `[]` when there is nothing to normalize.
 */
export const normalizeRegionCodes = (region: string | string[] | null | undefined): string[] => {
  if (!region) return [];
  const arr = Array.isArray(region) ? region : [region];
  return arr.map(item => {
    if (isRegionCode(item)) return item;
    const byName = NAME_TO_CODE.get(item.toLowerCase());
    return byName ?? item;
  });
};
